import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatNumber } from "@/lib/helper";
import { cn } from "@/lib/utils";
import { ColumnDef } from "@tanstack/react-table";
import { Ban, List, MoreVertical, ToggleRight } from "lucide-react";
import { VscEye } from "react-icons/vsc";
import { useCurrency } from "@/lib/useCurrency";
import {
  forwardRef,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useCopy } from "@/lib/useCopyToClipBoard";
import { BsBank } from "react-icons/bs";
import Img2 from "../../../../assets/qr.png";
import { CopyIcon, CopyIcon2 } from "@/assets";
import { useQuery } from "@tanstack/react-query";
import { getCryptoPayInAddress, getFiatPayIn } from "@/services/Home.service";
import { Label } from "@/components/ui/label";
import Selector from "@/components/Selector";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";

export type BalanceRow = {
  code: string;
  balance: number;
  pending: number;
};

function AssetCell({ code }: { code: string }) {
  const { handleSelectCurrency } = useCurrency();
  const currency = handleSelectCurrency(code);
  return (
    <div className="flex items-center gap-2">
      <img src={currency?.icon} alt="" />
      {currency?.name} ({code})
    </div>
  );
}

function BalanceValue({ code, value }: { code: string; value: number }) {
  const decimals = ["BTC", "ETH", "SOL", "BNB"].includes(code)
    ? value === 0
      ? 0
      : 8
    : code === "BUSD"
      ? value === 0
        ? 0
        : 4
      : value === 0
        ? 0
        : 2;
  return (
    <div className="flex items-center gap-2">
      {code === "NGN" ? code : null} {formatNumber(value, decimals)}{" "}
      {code !== "NGN" ? code : null}
    </div>
  );
}

export const columns: ColumnDef<BalanceRow>[] = [
  {
    accessorKey: "code",
    header: "Asset",
    cell: ({ row }) => <AssetCell code={row.original.code} />,
  },
  {
    accessorKey: "balance",
    header: "Available Balance",
    cell: ({ row }) => (
      <BalanceValue code={row.original.code} value={row.original.balance} />
    ),
  },
  {
    accessorKey: "pending",
    header: "Pending Balance",
    cell: ({ row }) => (
      <BalanceValue code={row.original.code} value={row.original.pending} />
    ),
  },

  {
    id: "actions",
    enableHiding: false,
    cell: ({ row }) => {
      return <DropdownWithDialogItemsSolution2 data={row?.original} />;
    },
  },
];

function DropdownWithDialogItemsSolution2({ data }: { data: BalanceRow }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hasOpenDialog, setHasOpenDialog] = useState(false);
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null);
  const focusRef = useRef<HTMLButtonElement | null>(null);

  function handleDialogItemSelect() {
    focusRef.current = dropdownTriggerRef.current;
  }

  function handleDialogItemOpenChange(open: boolean) {
    setHasOpenDialog(open);
    if (open === false) {
      setDropdownOpen(false);
    }
  }
  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="w-8 h-8 p-0"
          ref={dropdownTriggerRef}
        >
          <span className="sr-only">Open menu</span> <MoreVertical />{" "}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        sideOffset={5}
        hidden={hasOpenDialog}
        onCloseAutoFocus={(event) => {
          if (focusRef.current) {
            focusRef.current.focus();
            focusRef.current = null;
            event.preventDefault();
          }
        }}
      >
        <DialogItem
          triggerChildren={
            <>
              <VscEye />
              View account info
            </>
          }
          onSelect={handleDialogItemSelect}
          onOpenChange={handleDialogItemOpenChange}
          size="md"
        >
          <FundAccountOrViewInfo code={data.code} />
        </DialogItem>
        <DropdownMenuItem className="flex items-center gap-2 text-xs text-primary-9">
          <List />
          View transactions
        </DropdownMenuItem>
        <DropdownMenuItem className="flex items-center gap-2 text-xs text-primary-9">
          <ToggleRight />
          Enable asset
        </DropdownMenuItem>
        <DropdownMenuItem className="flex items-center gap-2 text-xs text-error">
          <Ban className="w-3 h-3" />
          Deactivate
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type DialogItemProps = Omit<
  ComponentPropsWithoutRef<typeof DropdownMenuItem>,
  "onSelect"
> & {
  triggerChildren: ReactNode;
  children: ReactNode;
  onSelect?: () => void;
  onOpenChange?: (open: boolean) => void;
  size?: string;
};

const DialogItem = forwardRef<HTMLDivElement, DialogItemProps>(
  (props, forwardedRef) => {
    const {
      triggerChildren,
      children,
      onSelect,
      onOpenChange,
      size,
      ...itemProps
    } = props;
    return (
      <Dialog onOpenChange={onOpenChange}>
        <DialogTrigger asChild>
          <DropdownMenuItem
            {...itemProps}
            ref={forwardedRef}
            className="cursor-pointer flex items-center gap-2 text-xs text-primary-9"
            onSelect={(event) => {
              event.preventDefault();
              onSelect?.();
            }}
          >
            {triggerChildren}
          </DropdownMenuItem>
        </DialogTrigger>
        <DialogContent
          className={cn(
            "p-0 text-left items-start rounded-[4px] bg-background",
            size ? `max-w-${size}` : "max-w-xs",
          )}
        >
          {children}
        </DialogContent>
      </Dialog>
    );
  },
);

const FundAccountOrViewInfo = ({ code }: { code?: string }) => {
  const [selectedNetwork, setSelectedNetwork] = useState<{
    label?: string;
    value?: string;
  }>({});
  const { copyToClipboard } = useCopy();
  const { handleSelectCurrency } = useCurrency();
  const type = handleSelectCurrency(code)?.type;

  const fiatCheck = code !== undefined && type === "fiat";
  const cryptoCheck =
    code !== undefined &&
    selectedNetwork.value !== undefined &&
    type === "crypto";
  const fiat = useQuery({
    queryKey: ["fiat"],
    queryFn: async () => await getFiatPayIn(code ?? ""),
    refetchOnMount: "always",
    enabled: !!fiatCheck,
  });
  const walletAddress = useQuery({
    queryKey: ["walletAddy", selectedNetwork?.value],
    queryFn: async () =>
      await getCryptoPayInAddress(code ?? "", selectedNetwork.value ?? ""),
    refetchOnMount: "always",
    enabled: !!cryptoCheck,
  });
  return (
    <>
      <div className="flex flex-col gap-8">
        <DialogHeader className="bg-off-white-1 py-6 px-8 rounded-t-[16px] flex flex-row items-center gap-4">
          <div className="w-12 h-12 rounded-full flex justify-center items-center bg-off-white-3 text-primary-3">
            <BsBank />
          </div>
          <div className="grid gap-[2px]">
            <DialogTitle className="text-lg font-medium text-primary-3">
              Fund account
            </DialogTitle>

            <DialogDescription className="text-sm text-gray-80">
              Top up your available balance{" "}
            </DialogDescription>
          </div>
        </DialogHeader>
      </div>
      <div className="px-8 py-3 grid gap-6">
        {type === "crypto" && (
          <>
            <div className="flex flex-col w-full items-start gap-1 border-[1px]  rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 justify-between">
              <Label className="text-xs text-gray-70">Select Network</Label>
              <Selector
                options={[
                  { label: "BEP-20", value: "bep20" },
                  { label: "TRC-20", value: "trc20" },
                  { label: "ERC-20", value: "erc20" },
                ]}
                selected={selectedNetwork}
                setSelected={setSelectedNetwork}
              />
            </div>
            <Separator orientation="horizontal" className="h-[1px] bg-border" />
          </>
        )}

        <div className="grid gap-5">
          <h4 className="text-gray-80 text-sm font-medium">
            Deposit information
          </h4>
          {code === undefined ? null : (
            <>
              {type === "crypto" ? (
                <div className="gap-6 flex flex-col p-6 rounded-[8px] border-[1px] border-off-white">
                  <img src={Img2} className="mx-auto w-[136px] h-[136px]" />
                  <div className="flex text-xs text-gray-80 gap-3 items-center">
                    <div className="w-full">
                      <Separator
                        orientation="horizontal"
                        className="h-[1px] w-full bg-border"
                      />
                    </div>
                    Or
                    <div className="w-full">
                      <Separator
                        orientation="horizontal"
                        className="h-[1px] w-full bg-border"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3 justify-between">
                    {walletAddress?.isFetching ? (
                      <Skeleton className="h-12 w-full" />
                    ) : (
                      <div className="flex flex-col w-full items-start gap-1 border-[1px]  rounded-[8px] px-2 py-2 h-12 border-[#EBE9EC] justify-between">
                        <Label className="text-xs text-gray-70">
                          Copy wallet address
                        </Label>
                        <Input
                          className="p-0 border-none h-4 bg-transparent w-full"
                          disabled
                          defaultValue={
                            walletAddress?.data?.data?.data?.address
                          }
                        />
                      </div>
                    )}
                    <Button
                      className="h-12 p-4 flex items-center gap-[5px]"
                      onClick={() =>
                        copyToClipboard(
                          walletAddress?.data?.data?.data?.address,
                        )
                      }
                    >
                      Copy
                      <CopyIcon />
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div className="grid">
                      <p className="text-gray-80 text-xs">Bank Name</p>
                      {fiat?.isFetching ? (
                        <Skeleton className="h-[14px] w-[120px]" />
                      ) : (
                        <h4 className="text-primary-3 text-sm font-medium">
                          {fiat?.data?.data?.data?.bankName}
                        </h4>
                      )}
                    </div>
                    <Button
                      className="p-0 h-0"
                      variant="ghost"
                      onClick={() =>
                        copyToClipboard(fiat?.data?.data?.data?.bankName)
                      }
                    >
                      <CopyIcon2 />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="grid">
                      <p className="text-gray-80 text-xs">Account Number</p>
                      {fiat?.isFetching ? (
                        <Skeleton className="h-[14px] w-[120px]" />
                      ) : (
                        <h4 className="text-primary-3 text-sm font-medium">
                          {fiat?.data?.data?.data?.accountNumber}
                        </h4>
                      )}
                    </div>
                    <Button
                      className="p-0 h-0"
                      variant="ghost"
                      onClick={() =>
                        copyToClipboard(fiat?.data?.data?.data?.accountNumber)
                      }
                    >
                      <CopyIcon2 />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="grid">
                      <p className="text-gray-80 text-xs">Account Name</p>
                      {fiat?.isFetching ? (
                        <Skeleton className="h-[14px] w-[120px]" />
                      ) : (
                        <h4 className="text-primary-3 text-sm font-medium">
                          {fiat?.data?.data?.data?.accountName}
                        </h4>
                      )}
                    </div>
                    <Button
                      className="p-0 h-0"
                      variant="ghost"
                      onClick={() =>
                        copyToClipboard(fiat?.data?.data?.data?.accountName)
                      }
                    >
                      <CopyIcon2 />
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
